import { action, computed } from 'mobx'
import type { AssignNextbookCubit } from '../AssignNextbookCubit'
import type { AssignNextbookFieldErrors } from '../types'
import { DateTime } from 'luxon'
import { sum } from '../../../util/arrays'
import { RoomState } from '../../../models/RoomState'

export class AssignNextbookValidationController {
  cubit: AssignNextbookCubit

  constructor(cubit: AssignNextbookCubit) {
    this.cubit = cubit
  }

  @action
  validate() {
    const currentErrors: Partial<AssignNextbookFieldErrors> = {}

    this.cubit.form.errors.clear()

    if (this.cubit.step === 'sketch_schedule') {
      this.validateSketchSchedule(currentErrors)
    } else if (this.cubit.step === 'confirm_schedule') {
      this.validateConfirmSchedule(currentErrors)
    } else if (this.cubit.step === 'configure_grouping') {
      this.validateConfirmGrouping(currentErrors)
    } else if (this.cubit.step === 'configure_grouping_roles') {
      this.validateConfirmGroupingRoles(currentErrors)
    } else if (this.cubit.step === 'configure_peer_reviews') {
      this.validateConfirmPeerReviews(currentErrors)
    }

    const isValid = Object.keys(currentErrors).length === 0

    if (!isValid) {
      this.cubit.form.errors.replace(currentErrors)
    }

    return isValid
  }

  // revalidate runs the validation and clears errors that have been fixed
  // it also does nothing if validation was never triggered
  @action
  revalidate() {
    const newErrors: Partial<AssignNextbookFieldErrors> = {}

    if (this.cubit.step === 'sketch_schedule') {
      this.validateSketchSchedule(newErrors)
    } else if (this.cubit.step === 'confirm_schedule') {
      this.validateConfirmSchedule(newErrors)
    } else if (this.cubit.step === 'configure_grouping') {
      this.validateConfirmGrouping(newErrors)
    } else if (this.cubit.step === 'configure_grouping_roles') {
      this.validateConfirmGroupingRoles(newErrors)
    } else if (this.cubit.step === 'configure_peer_reviews') {
      this.validateConfirmPeerReviews(newErrors)
    } else if (this.cubit.step === 'configure_grading') {
      this.validateConfirmGrading(newErrors)
    }

    const currentErrors = this.cubit.form.errors.map
    const keys = Array.from(currentErrors.keys())

    for (const key of keys) {
      if (!newErrors[key]) {
        this.cubit.form.errors.delete(key)
      }
    }
  }

  validateSketchSchedule(currentErrors: Partial<AssignNextbookFieldErrors>) {
    const currentFields = this.cubit.form.fields
    const firstDueDate = currentFields.get('firstDueDate')
    const ltiModeOn = this.cubit.ltiModeOn

    if (!ltiModeOn && !currentFields.get('sectionId')) {
      currentErrors.sectionId = {
        type: 'required',
        i18nKey: 'section_id_required',
      }
    }

    if (!firstDueDate) {
      currentErrors.firstDueDate = {
        type: 'required',
        i18nKey: 'first_due_date_required',
      }
    } else {
      if (firstDueDate < DateTime.now()) {
        currentErrors.firstDueDate = {
          type: 'invalid',
          i18nKey: 'first_due_date_invalid',
        }
      }
    }

    if (!currentFields.get('timeToComplete')) {
      currentErrors.timeToComplete = {
        type: 'required',
        i18nKey: 'time_to_complete_required',
      }
    }

    if (!currentFields.get('frequency')) {
      currentErrors.frequency = {
        type: 'required',
        i18nKey: 'frequency_required',
      }
    }

    return currentErrors
  }

  validateConfirmSchedule(currentErrors: Partial<AssignNextbookFieldErrors>) {
    const currentFields = this.cubit.form.fields
    const assignments = currentFields.get('assignments')

    if (!assignments || assignments.length === 0) {
      currentErrors.assignments = {
        type: 'required',
        i18nKey: 'assignments_required',
      }
      return false
    }

    return currentErrors
  }

  validateConfirmGrouping(currentErrors: Partial<AssignNextbookFieldErrors>) {
    const currentFields = this.cubit.form.fields
    const { min, max } = this.cubit.schedule.globalGroupConstraintsToUse
    const groupingOptions = currentFields.get('groupingOptions')

    if (!groupingOptions) {
      currentErrors.groupingOptions = {
        type: 'required',
        i18nKey: 'grouping_options_required',
      }
      return currentErrors
    }

    if (
      !groupingOptions.groupingSize ||
      groupingOptions.groupingSize < min ||
      groupingOptions.groupingSize > max
    ) {
      currentErrors.groupingOptions = {
        type: 'invalid',
        i18nKey: 'grouping_size_invalid',
        args: {
          min,
          max,
        },
      }
    }

    return currentErrors
  }

  validateConfirmGroupingRoles(
    currentErrors: Partial<AssignNextbookFieldErrors>
  ) {
    const currentFields = this.cubit.form.fields
    const groupingOptions = currentFields.get('groupingOptions')
    const assignments = currentFields.get('assignments') ?? []

    // should never make it here, but include for TS reasons
    if (!groupingOptions) {
      currentErrors.groupingOptions = {
        type: 'required',
        i18nKey: 'grouping_options_required',
      }
      return currentErrors
    }

    const { groupingSize } = groupingOptions

    for (let i = 0; i < assignments.length; i++) {
      const assignment = assignments[i]
      const { slideDeckId, groupSizeOverride } = assignment
      const effectiveGroupSize = groupSizeOverride || groupingSize
      const constraints =
        this.cubit.schedule.groupSizeConstraintsBySlideDeckId.get(
          slideDeckId
        ) || { min: 1, max: RoomState.maxAllowedUsers }

      // if any are invalid, set just the one error and return
      // we can't delineate errors per-assignment with this structure
      // so will need a better approach if we want that in the future
      // for now we will construct all relevant issues in the UI
      if (
        effectiveGroupSize < constraints.min ||
        effectiveGroupSize > constraints.max
      ) {
        currentErrors.assignments = {
          type: 'invalid',
          i18nKey: 'grouping_size_invalid',
          args: {
            min: constraints.min,
            max: constraints.max,
          },
        }
        return currentErrors
      }
    }
  }

  validateConfirmPeerReviews(
    currentErrors: Partial<AssignNextbookFieldErrors>
  ) {
    const currentFields = this.cubit.form.fields
    const peerReviewOptions = currentFields.get('peerReviewOptions')

    if (!peerReviewOptions) {
      currentErrors.peerReviewOptions = {
        type: 'required',
        i18nKey: 'peer_review_options_required',
      }
      return currentErrors
    }

    if (!peerReviewOptions.peerReviewsEnabled) {
      return currentErrors // No further validation needed
    }

    const criteria = peerReviewOptions.peerReviewCriteria
    if (!criteria || criteria.length === 0) {
      currentErrors.peerReviewOptions = {
        type: 'invalid',
        i18nKey: 'peer_review_criteria_invalid',
      }
      return currentErrors
    }

    for (const criterion of criteria) {
      if (
        !criterion.prQuestion ||
        criterion.prQuestion.trim().length === 0 ||
        criterion.prQuestion.length > 30
      ) {
        currentErrors.peerReviewOptions = {
          type: 'invalid',
          i18nKey: 'peer_review_criteria_invalid',
        }
        return currentErrors
      }
      if (
        !criterion.prDescription ||
        criterion.prDescription.trim().length === 0 ||
        criterion.prDescription.length > 180
      ) {
        currentErrors.peerReviewOptions = {
          type: 'invalid',
          i18nKey: 'peer_review_criteria_invalid',
        }
        return currentErrors
      }
    }

    return currentErrors
  }

  @computed
  get isGradingValid() {
    const currentFields = this.cubit.form.fields
    const groupingOptions = currentFields.get('gradingOptions')
    if (groupingOptions && groupingOptions.configureGrading) {
      if (!groupingOptions.scalars) return false
      const total = sum(Object.values(groupingOptions.scalars))
      if (total !== 1) return false
    }
    return true
  }

  validateConfirmGrading(currentErrors: Partial<AssignNextbookFieldErrors>) {
    const currentFields = this.cubit.form.fields
    const groupingOptions = currentFields.get('gradingOptions')

    if (groupingOptions && groupingOptions.configureGrading === true) {
      if (!groupingOptions.scalars) {
        currentErrors.gradingOptions = {
          type: 'required',
          i18nKey: 'grading_options_required',
        }
      } else {
        const scalars = groupingOptions.scalars
        const sum = Object.values(scalars).reduce((a, b) => a + b, 0)

        if (sum !== 1) {
          currentErrors.gradingOptions = {
            type: 'invalid',
            i18nKey: 'grading_options_invalid',
          }
        }
      }
    }

    return currentErrors
  }
}
